<script setup>
    import { useStageStore } from '@/stores/stage';
    import { computed, defineProps } from 'vue'
    import { useI18n } from 'vue-i18n'

    const formStorage = useStageStore();
    const { t } = useI18n();

    const props = defineProps({
        stage: Object,
        route: String
    })

    const stageIds = computed(() => {
        return formStorage.getUnAnsweredStages.map(stage => stage.id)
    })

    /**
     * Provide array of target ids with no existing answers
     */
    const excludedStageOfSamePath = computed(() => {
        return formStorage.getAllConnectors.filter(connector => stageIds.value
            .includes(connector.source) && stageIds.value.includes(connector.target))
            .map(s => s.target)
            // .at(-1)
    })

    const stagesThatCanBeMovedInto = computed(() => {

        // Get array of connectors that form path to current stage
        var pathToCurrentStageArray = [];
        formStorage.getPathToCurrentStageRecursively(props.stage.id, pathToCurrentStageArray)
        
        // Get stages by travelling connectors path step-by-step from current stage
        const availableStages = getAvailableStages(pathToCurrentStageArray)

        return availableStages.filter(stage => {
            return showLink(stage);
        })
    })

    const getAvailableStages = (connectorsArray) => {
        var resultsArray = [];
        for (const connector of connectorsArray.reverse()) {
            if (resultsArray.length) {
                break;
            }

            const sourceStage = formStorage.getStageById(connector.source)[0];
            if (!sourceStage?.children.length) {
                continue;
            }

            getAvailableChildStagesRecursively(sourceStage.children, 0, resultsArray)
        }

        return resultsArray;
    }

    const getAvailableChildStagesRecursively = (childrenArray, recursionLevel, resultsArray) => {
        var availableChildStages = [];

        // Check if one or more direct childs are available before trying to look for sub-childs
        for (const childId of childrenArray) {

            if (stageIds.value.includes(childId)) {
                const childStage = formStorage.getStageById(childId)[0];

                // Allow stage addition if it can accessed
                if (
                    childStage.access.map &&
                    childStage.access.question &&
                    childStage.access.questionAccessAllowedByRelations
                ) {
                    availableChildStages.push(childStage)
                }
            }
        }

        if (availableChildStages.length) {
            resultsArray.push(...availableChildStages);
            return;
        }

        // If no direct child matches are made, continue to check for sub-child items for matches
        for (const childId of childrenArray) {
            if (availableChildStages.length) {
                break;
            }

            const childStage = formStorage.getStageById(childId)[0];

            // Include stage if it is still unanswered
            if (stageIds.value.includes(childStage.id)) {

                // Allow stage addition if it can accessed
                if (
                    childStage.access.map &&
                    childStage.access.question &&
                    childStage.access.questionAccessAllowedByRelations
                ) {
                    availableChildStages.push(childStage)
                }
            }

            if (!childStage?.children.length) {
                continue;
            }

            getAvailableChildStagesRecursively(childStage.children, recursionLevel + 1, availableChildStages)
        }

        // Include results into primary resultsArray
        resultsArray.push(...availableChildStages)
    }

    const showLink = (nextStep) => {
        const sameAsCurrentStage = nextStep.id === formStorage.stage;
        const nextStepHasChildren = formStorage.getStageById(nextStep.id)[0].children.length > 0;

        const idIsExcluded = excludedStageOfSamePath.value.includes(nextStep.id);
        const nextStepHasTitle = nextStep.title[formStorage.language] !== undefined;

        if (sameAsCurrentStage && !nextStepHasChildren) {
            return false;
        }
        if (idIsExcluded) {
            return false;
        }
        if (!nextStepHasTitle) {
            return false;
        }

        return true;
    }

    const hasQuestionAccess = (nextStepId) => {
        return formStorage.getStageById(nextStepId)[0].access.question
    }
</script>

<template>
  <div v-if="formStorage.getUnAnsweredStages.length > 0">
    <p>{{ t('end-of-path.continue') }}</p>
  </div>
  <div v-else>
    <p>{{ t('end-of-path.completed') }}</p>
  </div>

  <div>
      <nav class="navbar end-of-survey-navigation-bar" :aria-label="t('form-steps')" v-if="formStorage.getUnAnsweredStages.length > 0">
          <ul class="list-unstyled navbar-nav flex-row">
              <template v-for="nextStep in stagesThatCanBeMovedInto" :key="nextStep.id">
                  <li class="nav-item">
                      <router-link
                          v-if="hasQuestionAccess(nextStep.id)"
                          :to="`/${nextStep.id}`"
                          class="link-button"
                          :style="`border-color: ${formStorage.getPathDetailsByStageId(Number(nextStep.id))[0]?.color}`"
                      >
                          {{ nextStep.title[formStorage.language] }}
                      </router-link>
                      <span
                          v-else
                          class="link-button disabled"
                      >
                          {{ nextStep.title[formStorage.language] }}
                      </span>
                  </li>
              </template>
          </ul>
      </nav>
  </div>
  <div v-if="formStorage.getUnAnsweredStages.length > 0" style="margin-top: 0.75rem">
    <p>{{ t('end-of-path.save-or-send') }}</p>
  </div>

</template>